// 2026-09-23 - FormuLearning - 최적화 추천 진행 표시 최소판 (COMP-OPTIMIZATION-PROGRESS, 이슈 #67 S4-4)
// 2026-09-23 수정 - 이슈 #67 S4-7: 단계 목록에 **`AI 해설`**(`ai_summary`) — 반영이 해설(약 40초)과 반영으로 나뉜다
//                   (UI/UX v2.7 COMP-OPTIMIZATION-PROGRESS · BR-OPT-13). 해설이 `추천 탐색` 다음으로 길다.
// 2026-09-23 수정 - 이슈 #83: 카드 안 **Break**(`onBreak`) · 단계 변화를 부모에 알린다(`onStage` — 헤더 진행 표시).
// 2026-11-03 수정 - 이슈 #114: viewer(조회 전용)는 진행 표시만 보고 Break 는 비활성.
// 파일 위치: frontend/src/features/step4/OptimizationProgress.tsx
// 목적: 추천이 작업(수십 초~수 분)이 되면서 동기 응답이 없어졌다 — 등록된 작업이 반영될 때까지 **현재 단계 +
//       경과 시간**을 보이고, 끝나면 부모에 알린다(UI/UX v2.6 §5.9 · 프로세스 BR-OPT-13). S4-4 최소판:
//       단계·경과·학습 제외 배치. `AI 해설` 단계는 S4-7 에서 더했다(S4-6 반영 백그라운드화의 화면 짝).
//       남은 시간은 추정하지 않는다. 스트림이 끊기면 작업 조회로 확인하고 다시 붙는다(작업은 계속된다).

import { useEffect, useRef, useState } from 'react'
import { useCanWrite, READ_ONLY_TITLE } from '../../hooks/useCanWrite'
import {
  getOptimizationJob, streamOptimizationJob,
  type ExcludedBatch, type OptimizationJobSummary, type OptimizationStreamEvent,
} from '../../api/step4'

type Stage = OptimizationJobSummary['stage']
export type ProgressEnd = Extract<OptimizationStreamEvent, { event: 'done' | 'error' }>

const STEPS: { label: string; stages: Stage[] }[] = [
  { label: '적재·검증', stages: ['load'] },
  { label: '모델 적합', stages: ['fit'] },
  { label: '추천 탐색', stages: ['search'] },
  { label: '예측 프로파일', stages: ['profile', 'write'] },
  { label: 'AI 해설', stages: ['ai_summary'] },
  { label: '반영', stages: ['apply'] },
]

const F2_REASON: Record<string, string> = {
  insufficient_points: '유효 시점 부족(15분 내 85% 초과 등)',
  no_test: '시험약 용출 없음',
  no_reference: '대조약 용출 없음',
  no_overlap: '측정 시점이 겹치지 않음',
  no_common_solution: '공통 용출액 없음',
}

/** 학습 제외 사유(인터페이스 §4.3 어휘) → 사람이 읽는 문구. */
function excludedReason(e: ExcludedBatch): string {
  if (e.reason === 'missing_x') return 'X 성분 값 없음'
  if (e.reason === 'composition_sum') return '조성 합이 100% 가 아님'
  if (e.reason === 'missing_y') return 'Y 미입력'
  if (e.reason === 'excluded') return '제외 배치'
  if (e.reason.startsWith('f2_unavailable:')) {
    const key = e.reason.slice('f2_unavailable:'.length)
    return `pH ${key} f2 계산 불가${e.f2_reason ? `(${F2_REASON[e.f2_reason] ?? e.f2_reason})` : ''}`
  }
  return e.reason
}

/** 학습에서 제외된 배치 — 접힌 한 줄, 펼치면 배치별 사유(UI/UX §5.9). 422 no_training_rows 에도 쓴다. */
export function ExcludedList({ items }: { items: ExcludedBatch[] }) {
  const [open, setOpen] = useState(false)
  if (items.length === 0) return null
  return (
    <div className="mt-1 text-[10px] text-text-secondary">
      <button type="button" className="underline-offset-2 hover:underline" onClick={() => setOpen((v) => !v)}>
        학습에서 제외된 배치 {items.length}건 {open ? '▾' : '▸'}
      </button>
      {open && (
        <ul className="mt-1 space-y-0.5 pl-3">
          {items.map((e) => (
            <li key={e.batch_id}>{e.batch_code} · {excludedReason(e)}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

function fmt(sec: number): string {
  const s = Math.max(0, Math.floor(sec))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

function finished(j: OptimizationJobSummary): boolean {
  if (j.status === 'failed' || j.status === 'cancelled') return true
  return j.status === 'succeeded' && (j.job_type !== 'suggest' || j.applied_at != null)
}

interface Props {
  projectId: string
  jobId: string
  excluded: ExcludedBatch[]
  /** `done`(반영까지 끝남) 또는 `error`(실패·반영 실패·중단). 한 번만 부른다. */
  onEnd: (end: ProgressEnd) => void
  /** 단계가 바뀔 때 — 헤더 진행 표시(`busyStore`)용. index 는 `STEPS` 위치(대기 중 -1), total 은 단계 수. */
  onStage?: (p: { index: number; total: number; label: string }) => void
  /** 중단(확인창 포함, #83). 없으면 Break 를 두지 않는다. */
  onBreak?: () => void
  breaking?: boolean
}

export default function OptimizationProgress({ projectId, jobId, excluded, onEnd, onStage, onBreak, breaking }: Props) {
  const canWrite = useCanWrite()
  const [stage, setStage] = useState<Stage>(null)
  // 경과 시간 = 서버가 준 값 + 받은 뒤 흐른 시간(스트림은 5초마다 갱신한다)
  const [base, setBase] = useState<{ sec: number; at: number }>(() => ({ sec: 0, at: Date.now() }))
  const [now, setNow] = useState(() => Date.now())
  const endRef = useRef(onEnd)
  useEffect(() => {
    endRef.current = onEnd
  }, [onEnd])

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])

  useEffect(() => {
    const ctrl = new AbortController()
    let ended = false
    const end = (e: ProgressEnd) => {
      if (ended) return
      ended = true
      endRef.current(e)
    }
    const run = async () => {
      while (!ended && !ctrl.signal.aborted) {
        try {
          await streamOptimizationJob(projectId, jobId, (ev) => {
            if (ev.event === 'stage') {
              setStage(ev.data.stage)
              setBase({ sec: ev.data.elapsed_sec, at: Date.now() })
            } else {
              end(ev)
            }
          }, ctrl.signal)
        } catch {
          if (ctrl.signal.aborted) return
        }
        if (ended || ctrl.signal.aborted) return
        // 스트림이 끝·끊김 없이 닫혔다 — 작업 조회로 확인하고, 안 끝났으면 다시 붙는다
        await new Promise((r) => setTimeout(r, 3000))
        try {
          const view = await getOptimizationJob(projectId, jobId)
          if (finished(view)) {
            end(view.status === 'succeeded'
              ? { event: 'done', data: { job: view, round: view.round, batches: view.batches, excluded: view.excluded } }
              : { event: 'error', data: { job: view } })
          }
        } catch {
          // 조회 실패 — 다음 바퀴에서 스트림부터 다시 시도한다
        }
      }
    }
    void run()
    return () => ctrl.abort()
  }, [projectId, jobId])

  const at = STEPS.findIndex((s) => s.stages.includes(stage))
  const elapsed = base.sec + (now - base.at) / 1000
  const label = stage == null ? '대기 중' : (STEPS[at]?.label ?? stage)

  useEffect(() => {
    onStage?.({ index: at, total: STEPS.length, label })
  }, [at, label, onStage])

  return (
    <div
      className="mb-3 rounded border px-3 py-2 text-[11px] text-text-body"
      data-testid="optimization-progress"
      style={{ borderColor: 'var(--border-purple)', backgroundColor: 'var(--bg-chip-purple)' }}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-semibold text-accent-purple">{label}</span>
        <span className="text-text-secondary">{fmt(elapsed)} 경과</span>
        <span className="ml-auto text-[10px] text-text-muted">추천은 보통 1분 안팎 걸립니다 — 창을 닫아도 계산은 계속됩니다</span>
        {onBreak && (
          <button
            type="button"
            onClick={onBreak}
            disabled={breaking || !canWrite}
            title={canWrite ? undefined : READ_ONLY_TITLE}
            className="rounded px-2 py-0.5 text-[10px] font-semibold text-red-400 transition-colors hover:bg-red-500/10 disabled:opacity-50"
            data-testid="optimization-break"
          >
            {breaking ? 'Breaking...' : 'Break'}
          </button>
        )}
      </div>
      <ol className="mt-1 flex flex-wrap gap-x-3 text-[10px] text-text-secondary">
        {STEPS.map((s, i) => (
          <li key={s.label} className={i === at ? 'font-semibold text-text-body' : ''}>
            {at > i ? '✓ ' : i === at ? '▸ ' : ''}{s.label}
          </li>
        ))}
      </ol>
      <ExcludedList items={excluded} />
    </div>
  )
}
